"use client";

import { useSearchParams } from "next/navigation";
import { Suspense } from "react";

import {
  ROUTE_GA_IDS,
  RouteGoogleAnalytics,
} from "@/components/analytics/route-google-analytics";
import { ExpertSignIn } from "@/components/auth/expert-sign-in";

interface ExpertLoginFormProps {
  referralDisclosure?: string;
}

function ExpertLoginContent({ referralDisclosure }: ExpertLoginFormProps) {
  const searchParams = useSearchParams();

  const returnUrl = searchParams?.get("returnUrl") ?? undefined;
  const redirectTo = searchParams?.get("redirectTo") ?? undefined;
  const onboardingInterviewId =
    searchParams?.get("onboardingInterviewId") ?? undefined;
  const reason = searchParams?.get("reason");
  const isBanned = reason === "banned";
  const isExpertEmail = reason === "expert-email";
  // Experts sign in with OAuth. `?magic-link=1` adds the emailed-link form for
  // the few partners who cannot use any of those providers; hand them the URL
  // out of band.
  const showMagicLink = searchParams?.get("magic-link") === "1";

  // When login was initiated by an OAuth 2.1 authorization request (the Terac
  // Recorder desktop app / MCP clients hit /oauth2/authorize while signed
  // out), the oauth-provider redirects here with the full *signed* authorize
  // query (sig + exp + PKCE).
  // After sign-in we must send the user back to the authorize endpoint with
  // that exact query so the provider resumes the flow and issues a code to the
  // loopback client. Without this the user just lands on /me and the desktop
  // app never receives its code.
  const oauthContinueUrl =
    searchParams?.get("client_id") &&
    searchParams?.get("sig") &&
    searchParams?.get("response_type") === "code"
      ? `/api/auth/oauth2/authorize?${searchParams.toString()}`
      : undefined;

  return (
    <ExpertSignIn
      mode="login"
      returnUrl={returnUrl}
      redirectTo={redirectTo}
      onboardingInterviewId={onboardingInterviewId}
      isBanned={isBanned}
      isExpertEmail={isExpertEmail}
      oauthContinueUrl={oauthContinueUrl}
      showMagicLink={showMagicLink}
      referralDisclosure={referralDisclosure}
    />
  );
}

export function ExpertLoginForm({ referralDisclosure }: ExpertLoginFormProps) {
  return (
    <>
      <RouteGoogleAnalytics gaId={ROUTE_GA_IDS.login} />
      <Suspense>
        <ExpertLoginContent referralDisclosure={referralDisclosure} />
      </Suspense>
    </>
  );
}
